/**
 * <gh-size-guide> — size guide dialog (Gaucho House, sections/gh-size-guide.liquid).
 *
 * - Any element with `data-gh-size-guide-open` opens the first guide on the page (the section
 *   only renders on products it applies to, so the first one is the one that applies).
 *   Inside the quick view it opens the guide that assets/quick-add.js mounted for the product shown.
 * - Native modal <dialog>: focus stays inside while open and returns to the trigger on close.
 *   Closes with the X, Esc, a click on the overlay and, on phones, by dragging the grabber down.
 *   The page behind doesn't scroll (Horizon's lockScroll).
 * - The row of the size chosen in the buy box is highlighted, and follows size changes.
 * - Opens on load when the URL ends in #size-guide.
 */
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';

const CLOSE_FALLBACK_MS = 320;
/** Holder of the guide that assets/quick-add.js mounts for the quick view */
const QUICK_VIEW_SIZE_GUIDE_ID = 'GhQuickViewSizeGuide';
const DRAG_CLOSE_RATIO = 0.25;

class GhSizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-gh-size-guide-dialog]');
    this.panel = this.querySelector('[data-gh-size-guide-panel]');
    this.grabber = this.querySelector('[data-gh-size-guide-grabber]');
    if (!this.dialog || !this.panel) return;

    this.onClick = this.onClick.bind(this);
    this.onCancel = (event) => {
      event.preventDefault();
      this.close();
    };
    this.onDialogClose = () => this.afterClose();
    this.onSizeChange = (event) => {
      if (event.target instanceof Element && event.target.closest('variant-picker')) this.highlightSelectedSize();
    };
    this.onSectionSelect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close();
    };
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.grabber?.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('change', this.onSizeChange);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:block:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);

    this.highlightSelectedSize();

    // Links to a product with #size-guide (e.g. the collection page's size help strip) open the guide
    if (window.location.hash === '#size-guide' && !this.closest(`#${QUICK_VIEW_SIZE_GUIDE_ID}`)) {
      requestAnimationFrame(() => this.open());
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('cancel', this.onCancel);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.grabber?.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('change', this.onSizeChange);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:block:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    unlockScroll(this);
  }

  /** @param {Element | null} [trigger] Element that gets the focus back on close */
  open(trigger = null) {
    if (!this.dialog || this.dialog.open) return;

    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.highlightSelectedSize();
    this.resetDrag();
    this.dialog.classList.remove('is-closing');
    this.dialog.showModal();
    lockScroll(this);
  }

  close() {
    if (!this.dialog?.open || this.closing) return;

    if (prefersReducedMotion()) {
      this.dialog.close();
      return;
    }

    this.closing = true;
    this.dialog.classList.add('is-closing');

    const finish = () => {
      if (!this.closing) return;
      this.closing = false;
      window.clearTimeout(this.closeTimer);
      this.dialog.close();
    };

    this.panel.addEventListener('animationend', finish, { once: true });
    this.closeTimer = window.setTimeout(finish, CLOSE_FALLBACK_MS);
  }

  afterClose() {
    this.closing = false;
    this.dialog.classList.remove('is-closing');
    this.resetDrag();
    unlockScroll(this);

    if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    this.trigger = null;
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // The dialog fills the viewport; a click on it outside the panel is a click on the overlay
    if (target === this.dialog) {
      this.close();
      return;
    }

    if (target.closest('[data-gh-size-guide-close]')) this.close();
  }

  /** Value of the size option currently chosen in the buy box, lowercased */
  getSelectedSize() {
    const optionName = (this.dataset.sizeOption || 'size').trim().toLowerCase();
    // The quick view's guide (assets/quick-add.js) follows the quick view's sizes
    const picker = this.closest(`#${QUICK_VIEW_SIZE_GUIDE_ID}`)
      ? document.querySelector('#quick-add-modal-content variant-picker')
      : document.querySelector('variant-picker[data-template-product-match="true"]') ||
        document.querySelector('variant-picker');
    if (!picker) return null;

    for (const input of picker.querySelectorAll('input[type="radio"]:checked')) {
      if ((input.dataset.optionName || '').trim().toLowerCase() === optionName) {
        return input.value.trim().toLowerCase();
      }
    }

    for (const select of picker.querySelectorAll('select')) {
      const option = select.selectedOptions[0];
      if (option && (option.dataset.optionName || '').trim().toLowerCase() === optionName) {
        return option.value.trim().toLowerCase();
      }
    }

    return null;
  }

  highlightSelectedSize() {
    const selected = this.getSelectedSize();

    for (const row of this.querySelectorAll('[data-gh-size]')) {
      const isSelected = selected !== null && row.getAttribute('data-gh-size') === selected;
      row.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        row.setAttribute('aria-current', 'true');
      } else {
        row.removeAttribute('aria-current');
      }
    }
  }

  /* Drag the drawer down from the grabber (phones) */

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    if (!this.dialog.open || event.button !== 0) return;

    this.dragStartY = event.clientY;
    this.dragStartTime = event.timeStamp;
    this.dragOffset = 0;
    this.panel.classList.add('is-dragging');
    this.grabber.setPointerCapture(event.pointerId);
    this.grabber.addEventListener('pointermove', this.onPointerMove);
    this.grabber.addEventListener('pointerup', this.onPointerUp);
    this.grabber.addEventListener('pointercancel', this.onPointerUp);
  }

  /** @param {PointerEvent} event */
  onPointerMove(event) {
    this.dragOffset = Math.max(0, event.clientY - this.dragStartY);
    this.panel.style.translate = `0 ${this.dragOffset}px`;
  }

  /** @param {PointerEvent} event */
  onPointerUp(event) {
    this.grabber.removeEventListener('pointermove', this.onPointerMove);
    this.grabber.removeEventListener('pointerup', this.onPointerUp);
    this.grabber.removeEventListener('pointercancel', this.onPointerUp);
    if (this.grabber.hasPointerCapture(event.pointerId)) this.grabber.releasePointerCapture(event.pointerId);

    const elapsed = Math.max(event.timeStamp - this.dragStartTime, 1);
    const velocity = this.dragOffset / elapsed;
    const shouldClose =
      this.dragOffset > this.panel.offsetHeight * DRAG_CLOSE_RATIO || (this.dragOffset > 40 && velocity > 0.5);

    this.panel.classList.remove('is-dragging');

    if (shouldClose) {
      this.close();
    } else {
      this.panel.style.translate = '';
    }
  }

  resetDrag() {
    this.dragOffset = 0;
    this.panel?.classList.remove('is-dragging');
    if (this.panel) this.panel.style.translate = '';
  }
}

if (!customElements.get('gh-size-guide')) {
  customElements.define('gh-size-guide', GhSizeGuide);

  // Triggers live in the buy box, which Horizon re-renders on variant change: delegate the clicks
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-gh-size-guide-open]') : null;
    if (!trigger) return;

    // A trigger inside the quick view opens the guide of the product shown there
    const guide = trigger.closest('#quick-add-dialog')
      ? document.querySelector(`#${QUICK_VIEW_SIZE_GUIDE_ID} gh-size-guide`)
      : document.querySelector('gh-size-guide');
    if (!(guide instanceof GhSizeGuide)) return;

    event.preventDefault();
    guide.open(trigger);
  });
}
